import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Spinner } from "flowbite-react";
import { toast } from "react-toastify";
import AwardVotingApi, { AwardVotingPeriod } from "../../Api/AwardVotingApi";
import { useAppSelector } from "../../Reducers/store";
import { UserLevelNames } from "../../Models/UserLevel";

function formatWhen(iso?: string) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
}

function statusLabel(period: AwardVotingPeriod) {
  if (!period.isPublished) return "Draft";
  if (period.isOpen) return "Open";
  return "Closed";
}

const AdminAwardsVotingPage: React.FC = () => {
  const [periods, setPeriods] = useState<AwardVotingPeriod[]>([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { userModel } = useAppSelector((state) => state.user);

  useEffect(() => {
    if (userModel && userModel.userLevelId !== UserLevelNames.SuperAdmin) {
      toast.error(
        "You do not have permission to access this page. Please contact an administrator if you believe this is an error."
      );
      navigate("/");
    }
  }, [userModel, navigate]);

  useEffect(() => {
    AwardVotingApi.getPeriods()
      .then((res) => setPeriods(res.data ?? []))
      .catch(() => toast.error("Unable to load voting periods."))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="flex justify-center p-12">
        <Spinner size="xl" />
      </div>
    );
  }

  return (
    <div className="p-4 w-full">
      <div className="flex justify-between items-center mb-4">
        <h1 className="text-2xl font-bold">Awards Voting</h1>
        <button
          className="bg-blue-600 text-white px-4 py-2 rounded"
          onClick={() => navigate("/admin/awards/new")}
        >
          + New Period
        </button>
      </div>

      <div className="overflow-auto border rounded">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-200">
            <tr>
              <th className="text-left px-4 py-2">Title</th>
              <th className="text-left px-4 py-2">Year</th>
              <th className="text-left px-4 py-2">Membership</th>
              <th className="text-left px-4 py-2">Starts</th>
              <th className="text-left px-4 py-2">Ends</th>
              <th className="text-left px-4 py-2">Status</th>
              <th className="text-left px-4 py-2">Votes</th>
              <th className="text-left px-4 py-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {periods.length === 0 ? (
              <tr>
                <td className="px-4 py-3 text-gray-500" colSpan={8}>
                  No voting periods yet.
                </td>
              </tr>
            ) : (
              periods.map((period) => (
                <tr key={period.id} className="border-t">
                  <td className="px-4 py-2">{period.title}</td>
                  <td className="px-4 py-2">{period.year}</td>
                  <td className="px-4 py-2">{period.subscriptionName || "—"}</td>
                  <td className="px-4 py-2">{formatWhen(period.startsUtc)}</td>
                  <td className="px-4 py-2">{formatWhen(period.endsUtc)}</td>
                  <td className="px-4 py-2">{statusLabel(period)}</td>
                  <td className="px-4 py-2">{period.voteCount}</td>
                  <td className="px-4 py-2">
                    <Link
                      to={`/admin/awards/${period.id}`}
                      className="text-blue-600 hover:underline"
                    >
                      Edit
                    </Link>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AdminAwardsVotingPage;
